import {
    ClessonResult,
    CustomFlowLessonStructure,
    SkillsLessonStructure,
    StudentClessonProblemResult,
} from '@yandex-int/k-common';
import { ANSWER_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { CriteriaMark, CRITERIA_MARK_VALUE } from '@yandex-int/k-common/client/api/defs/get-criteria-marks';
// @ts-ignore
import { CLESSON_MODES, PROBLEM_INNER_TYPES } from '@yandex-int/k-common/constants';
import { isSkillsLessonStructure } from '@yandex-int/k-common/utils/client/lesson/skills-lesson/skills-lesson';

import { AttemptId } from 'store/clesson-results/typings';

interface ClessonData {
    mode: CLESSON_MODES;
    lesson: {
        structure: CustomFlowLessonStructure | null;
    };
}

interface ClessonProblemsProgressData {
    max: number;
    items: Partial<Record<ANSWER_STATUSES, number>>;
}

interface ClessonStudentsProgressData {
    max: number;
    active: number;
}

export interface ClessonProgressData {
    problems: ClessonProblemsProgressData;
    students: ClessonStudentsProgressData;
}

function isProblemWithVerdict(problemResult: StudentClessonProblemResult) {
    return ![PROBLEM_INNER_TYPES.THEORY, PROBLEM_INNER_TYPES.EDITABLE].includes(problemResult.problem_type);
}

function getLastAnsweredProblemResults(
    problemResults: StudentClessonProblemResult[],
    structure: SkillsLessonStructure
) {
    return structure.items.reduce((result, skill) => {
        skill.main.forEach((stack) => {
            for (let i = stack.length - 1; i >= 0; i -= 1) {
                const lastAnswered = problemResults.find(
                    (problemResult) => problemResult.problem_id === stack[i] && problemResult.answered
                );

                if (lastAnswered) {
                    result.push(lastAnswered);
                    return;
                }
            }

            const stackFirstProblem = problemResults.find((problemResult) => problemResult.problem_id === stack[0]);

            if (stackFirstProblem) {
                result.push(stackFirstProblem);
            }
        });

        return result;
    }, [] as StudentClessonProblemResult[]);
}

const defaultClessonProgressRatio = {
    [ANSWER_STATUSES.CORRECT]: 0,
    [ANSWER_STATUSES.INCORRECT]: 0,
    [ANSWER_STATUSES.NOT_EVALUATED]: 0,
};

function getClessonProgressRatio(
    clessonResult: ClessonResult,
    criteriaMarks: Record<AttemptId, CriteriaMark[]>,
    clesson: ClessonData
) {
    let max = 0;
    const items = { ...defaultClessonProgressRatio };

    const problemResults = clessonResult.students
        .map((student) => {
            const structure = clesson.lesson.structure;
            if (isSkillsLessonStructure(structure)) {
                return getLastAnsweredProblemResults(student.problems, structure);
            }

            return student.problems;
        })
        .flat();

    problemResults.forEach((problemResult) => {
        const attemptId = getOpenAnswerAttemptId(problemResult);
        const problemCriteriaMarks = attemptId ? criteriaMarks[attemptId] : null;
        if (problemCriteriaMarks) {
            problemCriteriaMarks.forEach((criteriaMark) => {
                switch (criteriaMark.value) {
                    case CRITERIA_MARK_VALUE.YES:
                        items[ANSWER_STATUSES.CORRECT] += 1;
                        break;
                    case CRITERIA_MARK_VALUE.NO:
                        items[ANSWER_STATUSES.INCORRECT] += 1;
                        break;
                }
            });

            max += problemCriteriaMarks.length;

            return;
        }

        if (!isProblemWithVerdict(problemResult)) {
            return;
        }

        if (problemResult.answered) {
            if (problemResult.status === ANSWER_STATUSES.CORRECT) {
                items[ANSWER_STATUSES.CORRECT] += 1;
            } else if (problemResult.status === ANSWER_STATUSES.INCORRECT) {
                items[ANSWER_STATUSES.INCORRECT] += 1;
            } else if (clesson.mode === CLESSON_MODES.CONTROL_WORK) {
                items[ANSWER_STATUSES.NOT_EVALUATED] += 1;
            }
        }

        max += 1;
    });

    return { items, max };
}

export function getClessonProgress(
    clessonResult: ClessonResult | null,
    criteriaMarks: Record<AttemptId, CriteriaMark[]>,
    clesson: ClessonData
): ClessonProgressData {
    if (!clessonResult) {
        return {
            problems: { max: 0, items: { ...defaultClessonProgressRatio } },
            students: { active: 0, max: 0 },
        };
    }

    return {
        problems: getClessonProgressRatio(clessonResult, criteriaMarks, clesson),
        students: {
            active: clessonResult.students.filter(({ problems }) => problems.some(({ time }) => time > 0)).length,
            max: clessonResult.students.length,
        },
    };
}

export function getOpenAnswerAttemptId(problemResult: StudentClessonProblemResult) {
    const attempt = Object.values(problemResult.marker_specific_data || {})[0]?.check_uuid;
    return attempt || null;
}
